// src/components/admin/WelcomeCardMemberEditorModal.jsx
import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { toast } from "react-toastify";
import ApiCustom from "../../utils/ApiCustom";
import {
  ArrowPathIcon,
  XMarkIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

const JSON_HEADERS = { headers: { "Content-Type": "application/json" } };

const FONT_TNR = `"Times New Roman", Times, serif`;
const BASE_MD_WIDTH = 768;

/* ===== Template variables (token + preview label) =====
   - Lưu schema phải là token {{...}}
   - Khi preview trong editor: render ra dạng [ ... ] để user nhìn hiểu
*/
const TEMPLATE_VARS = {
  companyName: { token: "{{companyName}}", preview: "[công ty]" },
  establishedDate: {
    token: "{{establishedDate}}",
    preview: "[ngày thành lập]",
  },
  businessAddress: { token: "{{businessAddress}}", preview: "[địa chỉ]" },
  companyPhone: { token: "{{companyPhone}}", preview: "[sđt doanh nghiệp]" },
  companyEmail: { token: "{{companyEmail}}", preview: "[email doanh nghiệp]" },
  websiteOrHotline: {
    token: "{{websiteOrHotline}}",
    preview: "[website/hotline]",
  },
  businessField: { token: "{{businessField}}", preview: "[sản phẩm/dịch vụ]" },

  repUserFullName: {
    token: "{{repUserFullName}}",
    preview: "[người đại diện]",
  },
  repUserPosition: { token: "{{repUserPosition}}", preview: "[chức vụ]" },
  repUserPhone: { token: "{{repUserPhone}}", preview: "[sđt người đại diện]" },
  repUserEmail: {
    token: "{{repUserEmail}}",
    preview: "[email người đại diện]",
  },
};

const renderTemplatePreviewText = (text = "") =>
  String(text || "").replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (m, key) => {
    return TEMPLATE_VARS[key]?.preview ?? m;
  });

/* ===== Permissions ===== */
const isSuperAdmin = () => String(localStorage.getItem("userId")) === "1";
const getUserPermissions = () => {
  try {
    const raw = localStorage.getItem("permissions");
    return raw ? JSON.parse(raw).map((p) => p.permissionSlug) : [];
  } catch {
    return [];
  }
};
const hasPermission = (slug) =>
  isSuperAdmin() || getUserPermissions().includes(slug);

/* ===== API ===== */
const API = {
  listTemplates: "/welcome-card-template-show",
  getMemberOverride: (companyId) =>
    `/welcome-card-member-template-show/${companyId}`,
  saveMemberOverride: (companyId) =>
    `/welcome-card-member-template-save/${companyId}`,
};

/* ===== Helpers ===== */
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const toPct = (px, base) => (base ? (px / base) * 100 : 0);

const extractList = (res) => {
  const payload = res?.data?.data ?? res?.data ?? null;

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;

  return [];
};

const pickActiveTemplate = (items) => {
  const list = Array.isArray(items) ? items : [];
  const actives = list.filter((x) => !!x?.isActive);
  const pool = actives.length ? actives : list;
  if (!pool.length) return null;

  return pool
    .slice()
    .sort((a, b) => Number(b?.version ?? 0) - Number(a?.version ?? 0))[0];
};

const safeParseSchema = (raw) => {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return null;
};

const genId = (prefix = "layer") =>
  `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;

/* ===== Default schema ===== */
const DEFAULT_SCHEMA = {
  schemaVersion: 2,
  backgroundSrc: "/welcome_user_template2.jpg",
  elements: {
    header: { top: 3, left: 0, width: 100, height: 8 },
    welcome: { top: 9, left: 0, width: 100, height: 16 },
    member: { top: 27, left: 0, width: 100, height: 14 },
    centerLogo: { top: 43, left: 0, width: 100, height: 18 },
    avatar: { top: 55, left: 15, width: 18, height: 18 },
    repInfo: { top: 56, left: 42, width: 55, height: 14, fontSize: 18 },
    companyInfo: { top: 70, left: 10, width: 80, height: 18, fontSize: 18 },
    footer: { bottom: 4, left: 0, width: 100, height: 10, fontSize: 18 },
  },
  assets: {
    topLogoSrc: "/logo192.png",
    centerLogoSrc: "/logo_welcome_v2.png",
  },
  memberBlock: {
    logoW: 160,
    logoH: 90,
    memberFontSize: 25,
  },
  texts: {
    headerLine1: "Hội Xây Dựng",
    headerLine2: "THÀNH PHỐ CẦN THƠ",
    welcomeTitle: "CHÀO MỪNG",
    memberLine1: "Là Hội viên chính thức của",
    memberLine2: "Hội Xây Dựng TP. Cần Thơ",
    repTitle: "NGƯỜI ĐẠI DIỆN THAM GIA",
    companyTitle: "THÔNG TIN DOANH NGHIỆP:",
    footerTitle: "SẢN PHẨM, DỊCH VỤ KINH DOANH:",
  },
  layers: [],
};

const mergeSchema = (loaded) => {
  const s = loaded || {};
  return {
    ...DEFAULT_SCHEMA,
    ...s,
    elements: { ...DEFAULT_SCHEMA.elements, ...(s.elements || {}) },
    assets: { ...DEFAULT_SCHEMA.assets, ...(s.assets || {}) },
    memberBlock: { ...DEFAULT_SCHEMA.memberBlock, ...(s.memberBlock || {}) },
    texts: { ...DEFAULT_SCHEMA.texts, ...(s.texts || {}) },
    layers: Array.isArray(s.layers) ? s.layers : [],
  };
};

/* ===== Data helpers ===== */
const pickLogo = (c) =>
  c?.userAvatar || c?.logo || c?.companyAvatar || c?.avatar || c?.logoUrl || "";

const fallbackAvatarUrl = (() => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200">
    <rect width="100%" height="100%" rx="100" fill="#0ea5e9"/>
    <text x="50%" y="55%" text-anchor="middle" font-size="80" fill="#fff">U</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
})();

/**
 * Các field cố định để "chèn vào" -> lưu token {{...}}
 * Preview trong editor sẽ hiển thị dạng [ ... ]
 */
const FIELD_DEFS = [
  {
    key: "rep.userFullName",
    label: "Người đại diện: Họ tên",
    prefix: "Người đại diện: ",
  },
  {
    key: "rep.userPosition",
    label: "Người đại diện: Chức vụ",
    prefix: "Chức vụ: ",
  },
  { key: "rep.userPhone", label: "Người đại diện: SĐT", prefix: "SĐT: " },
  { key: "rep.userEmail", label: "Người đại diện: Email", prefix: "Email: " },

  {
    key: "company.companyName",
    label: "Doanh nghiệp: Tên công ty",
    prefix: "",
  },
  {
    key: "company.establishedDate",
    label: "Doanh nghiệp: Ngày thành lập",
    prefix: "Ngày thành lập: ",
  },
  {
    key: "company.businessAddress",
    label: "Doanh nghiệp: Địa chỉ",
    prefix: "Địa chỉ: ",
  },
  {
    key: "company.userPhone",
    label: "Doanh nghiệp: SĐT",
    prefix: "Điện thoại: ",
  },
  { key: "company.userEmail", label: "Doanh nghiệp: Email", prefix: "Email: " },
  {
    key: "company.websiteOrHotline",
    label: "Doanh nghiệp: Website/Hotline",
    prefix: "Website: ",
  },
  {
    key: "company.businessField",
    label: "Doanh nghiệp: Ngành nghề",
    prefix: "",
  },
];

/* map field -> token (KHÔNG lấy data thật để lưu schema) */
const resolveFieldToken = (key) => {
  switch (key) {
    case "rep.userFullName":
      return TEMPLATE_VARS.repUserFullName.token;
    case "rep.userPosition":
      return TEMPLATE_VARS.repUserPosition.token;
    case "rep.userPhone":
      return TEMPLATE_VARS.repUserPhone.token;
    case "rep.userEmail":
      return TEMPLATE_VARS.repUserEmail.token;

    case "company.companyName":
      return TEMPLATE_VARS.companyName.token;
    case "company.establishedDate":
      return TEMPLATE_VARS.establishedDate.token;
    case "company.businessAddress":
      return TEMPLATE_VARS.businessAddress.token;
    case "company.userPhone":
      return TEMPLATE_VARS.companyPhone.token;
    case "company.userEmail":
      return TEMPLATE_VARS.companyEmail.token;
    case "company.websiteOrHotline":
      return TEMPLATE_VARS.websiteOrHotline.token;
    case "company.businessField":
      return TEMPLATE_VARS.businessField.token;
    default:
      return "—";
  }
};

const IMAGE_SOURCES = [
  { key: "", label: "Nhập URL ảnh (tự do)" },
  { key: "asset.topLogoSrc", label: "Assets: topLogoSrc" },
  { key: "asset.centerLogoSrc", label: "Assets: centerLogoSrc" },
  { key: "company.logo", label: "Doanh nghiệp: Logo" },
  { key: "rep.avatar", label: "Người đại diện: Avatar" },
];

const resolveImageSource = (key, schema, company, repUser) => {
  if (!key) return "";
  if (key === "asset.topLogoSrc") return schema?.assets?.topLogoSrc || "";
  if (key === "asset.centerLogoSrc") return schema?.assets?.centerLogoSrc || "";
  if (key === "company.logo") return pickLogo(company) || "";
  if (key === "rep.avatar") return repUser?.userAvatar || fallbackAvatarUrl;
  return "";
};

/* ===== V2 normalize + migrate =====
   QUY ƯỚC V2:
   - top/bottom: % theo CHIỀU CAO thiệp
   - left/width/height: % theo CHIỀU RỘNG thiệp
*/
function normalizeLayer(layer) {
  const type = layer?.type === "image" ? "image" : "text";
  const style = layer?.style || {};

  const out = {
    id: layer?.id || genId(type === "image" ? "img" : "txt"),
    type,

    left: Number(layer?.left ?? 0),
    width: Number(layer?.width ?? 20),
    height: Number(layer?.height ?? 6),
    zIndex: Number(layer?.zIndex ?? 10),

    top: layer?.bottom != null ? undefined : Number(layer?.top ?? 0),
    bottom: layer?.bottom != null ? Number(layer?.bottom) : undefined,

    text: type === "text" ? String(layer?.text ?? "") : undefined,

    src: type === "image" ? String(layer?.src ?? "") : undefined,
    fit:
      type === "image"
        ? layer?.fit === "contain"
          ? "contain"
          : "cover"
        : undefined,
    circle: type === "image" ? !!layer?.circle : undefined,
    sourceKey: type === "image" ? String(layer?.sourceKey ?? "") : undefined,

    style:
      type === "text"
        ? {
            fontFamily: style.fontFamily ?? FONT_TNR,
            fontSize: Number(style.fontSize ?? 18),
            fontWeight: String(style.fontWeight ?? "400"),
            color: style.color ?? "#ffffff",
            align: style.align ?? "left",
            uppercase: !!style.uppercase,
            lineHeight: Number(style.lineHeight ?? 1.25),
          }
        : undefined,
  };

  if (out.type === "image" && out.circle) out.height = out.width; // 1:1
  return out;
}

function ensureSchemaV2(loaded) {
  const s = mergeSchema(loaded);
  s.schemaVersion = 2;

  // already has layers
  if (Array.isArray(s.layers) && s.layers.length) {
    s.layers = s.layers
      .map((l) => normalizeLayer(l))
      .sort((a, b) => Number(a.zIndex) - Number(b.zIndex));
    return s;
  }

  // migrate from legacy -> default layers
  const el = s.elements || DEFAULT_SCHEMA.elements;
  const t = s.texts || DEFAULT_SCHEMA.texts;

  const mkText = (patch) =>
    normalizeLayer({
      type: "text",
      ...patch,
      style: {
        fontFamily: patch?.style?.fontFamily ?? FONT_TNR,
        fontSize: patch?.style?.fontSize ?? 18,
        fontWeight: patch?.style?.fontWeight ?? "600",
        color: patch?.style?.color ?? "#ffffff",
        align: patch?.style?.align ?? "center",
        uppercase: patch?.style?.uppercase ?? false,
        lineHeight: patch?.style?.lineHeight ?? 1.25,
      },
    });

  const mkImg = (patch) =>
    normalizeLayer({
      type: "image",
      ...patch,
      fit: patch?.fit ?? "contain",
      circle: !!patch?.circle,
      sourceKey: patch?.sourceKey ?? "",
      src: patch?.src ?? "",
    });

  const layers = [];

  // ===== HEADER group (logo 50px + gap 12 + text 320px, centered) =====
  const logoPx = 50;
  const gapPx = 12;
  const textPx = 320;
  const groupW = logoPx + gapPx + textPx;

  const groupLeftPct = ((BASE_MD_WIDTH - groupW) / 2 / BASE_MD_WIDTH) * 100;
  const logoLeftPct = groupLeftPct;
  const textLeftPct =
    (((BASE_MD_WIDTH - groupW) / 2 + logoPx + gapPx) / BASE_MD_WIDTH) * 100;
  const textWPct = (textPx / BASE_MD_WIDTH) * 100;

  layers.push(
    mkImg({
      id: "img_topLogo",
      top: el.header?.top ?? 3,
      left: logoLeftPct,
      width: (logoPx / BASE_MD_WIDTH) * 100,
      height: (logoPx / BASE_MD_WIDTH) * 100,
      zIndex: 20,
      sourceKey: "asset.topLogoSrc",
      fit: "contain",
    }),
  );

  layers.push(
    mkText({
      id: "txt_header1",
      top: (el.header?.top ?? 3) + 0.1,
      left: textLeftPct,
      width: textWPct,
      height: 3.0,
      zIndex: 21,
      text: t.headerLine1,
      style: { fontSize: 18, fontWeight: "600", align: "center" },
    }),
  );

  layers.push(
    mkText({
      id: "txt_header2",
      top: (el.header?.top ?? 3) + 2.2,
      left: textLeftPct,
      width: textWPct,
      height: 3.0,
      zIndex: 21,
      text: t.headerLine2,
      style: { fontSize: 18, fontWeight: "600", align: "center" },
    }),
  );

  // ===== WELCOME =====
  const padPx = 56; // px-14
  const welcomeLeftPct = (padPx / BASE_MD_WIDTH) * 100;
  const welcomeWidthPct = 100 - welcomeLeftPct * 2;

  layers.push(
    mkText({
      id: "txt_welcomeTitle",
      top: el.welcome?.top ?? 9,
      left: 0,
      width: 100,
      height: 5.5,
      zIndex: 22,
      text: t.welcomeTitle,
      style: { fontSize: 38, fontWeight: "400", align: "center" },
    }),
  );

  // IMPORTANT: companyName lưu token
  layers.push(
    mkText({
      id: "txt_companyName",
      top: (el.welcome?.top ?? 9) + 5.2,
      left: welcomeLeftPct,
      width: welcomeWidthPct,
      height: 8.0,
      zIndex: 23,
      text: TEMPLATE_VARS.companyName.token,
      style: {
        fontFamily: FONT_TNR,
        fontSize: 27,
        fontWeight: "700",
        color: "#facc15",
        align: "center",
        uppercase: true,
        lineHeight: 1.18,
      },
    }),
  );

  // ===== MEMBER block (logo 160x90) =====
  const logoW = s.memberBlock?.logoW ?? 160;
  const logoH = s.memberBlock?.logoH ?? 90;

  layers.push(
    mkImg({
      id: "img_companyLogo",
      top: el.member?.top ?? 27,
      left: ((BASE_MD_WIDTH - logoW) / 2 / BASE_MD_WIDTH) * 100,
      width: (logoW / BASE_MD_WIDTH) * 100,
      height: (logoH / BASE_MD_WIDTH) * 100,
      zIndex: 24,
      sourceKey: "company.logo",
      fit: "contain",
    }),
  );

  layers.push(
    mkText({
      id: "txt_member1",
      top: (el.member?.top ?? 27) + 9.8,
      left: 0,
      width: 100,
      height: 4.2,
      zIndex: 25,
      text: t.memberLine1,
      style: {
        fontSize: s.memberBlock?.memberFontSize ?? 25,
        fontWeight: "600",
        align: "center",
      },
    }),
  );

  layers.push(
    mkText({
      id: "txt_member2",
      top: (el.member?.top ?? 27) + 13.1,
      left: 0,
      width: 100,
      height: 4.2,
      zIndex: 25,
      text: t.memberLine2,
      style: {
        fontSize: s.memberBlock?.memberFontSize ?? 25,
        fontWeight: "600",
        align: "center",
      },
    }),
  );

  // ===== CENTER LOGO (122x150) =====
  const centerW = 122;
  const centerH = 150;
  layers.push(
    mkImg({
      id: "img_centerLogo",
      top: el.centerLogo?.top ?? 43,
      left: ((BASE_MD_WIDTH - centerW) / 2 / BASE_MD_WIDTH) * 100,
      width: (centerW / BASE_MD_WIDTH) * 100,
      height: (centerH / BASE_MD_WIDTH) * 100,
      zIndex: 26,
      sourceKey: "asset.centerLogoSrc",
      fit: "contain",
    }),
  );

  // ===== AVATAR (circle 18%) =====
  const avW = el.avatar?.width ?? 18;
  layers.push(
    mkImg({
      id: "img_repAvatar",
      top: el.avatar?.top ?? 55,
      left: el.avatar?.left ?? 15,
      width: avW,
      height: avW,
      zIndex: 27,
      sourceKey: "rep.avatar",
      fit: "cover",
      circle: true,
    }),
  );

  // ===== REP INFO =====
  const repLeft = el.repInfo?.left ?? 42;
  const repTop = el.repInfo?.top ?? 56;
  const repW = el.repInfo?.width ?? 55;
  const repFs = el.repInfo?.fontSize ?? 18;

  layers.push(
    mkText({
      id: "txt_repTitle",
      top: repTop,
      left: repLeft,
      width: repW,
      height: 4.2,
      zIndex: 28,
      text: t.repTitle,
      style: { fontSize: repFs, fontWeight: "700", align: "left" },
    }),
  );
  layers.push(
    mkText({
      id: "txt_repName",
      top: repTop + 3.9,
      left: repLeft,
      width: repW,
      height: 3.2,
      zIndex: 28,
      text: `Người đại diện: ${TEMPLATE_VARS.repUserFullName.token}`,
      style: { fontSize: repFs, fontWeight: "300", align: "left" },
    }),
  );
  layers.push(
    mkText({
      id: "txt_repPos",
      top: repTop + 6.8,
      left: repLeft,
      width: repW,
      height: 3.2,
      zIndex: 28,
      text: `Chức vụ: ${TEMPLATE_VARS.repUserPosition.token}`,
      style: { fontSize: repFs, fontWeight: "300", align: "left" },
    }),
  );
  layers.push(
    mkText({
      id: "txt_repPhone",
      top: repTop + 9.7,
      left: repLeft,
      width: repW,
      height: 3.2,
      zIndex: 28,
      text: `SĐT: ${TEMPLATE_VARS.repUserPhone.token}`,
      style: { fontSize: repFs, fontWeight: "300", align: "left" },
    }),
  );
  layers.push(
    mkText({
      id: "txt_repEmail",
      top: repTop + 12.6,
      left: repLeft,
      width: repW,
      height: 3.2,
      zIndex: 28,
      text: `Email: ${TEMPLATE_VARS.repUserEmail.token}`,
      style: { fontSize: repFs, fontWeight: "300", align: "left" },
    }),
  );

  // ===== COMPANY INFO =====
  const cLeft = el.companyInfo?.left ?? 10;
  const cTop = el.companyInfo?.top ?? 70;
  const cW = el.companyInfo?.width ?? 80;
  const cFs = el.companyInfo?.fontSize ?? 18;

  layers.push(
    mkText({
      id: "txt_companyTitle",
      top: cTop,
      left: cLeft,
      width: cW,
      height: 4.2,
      zIndex: 29,
      text: t.companyTitle,
      style: { fontSize: cFs, fontWeight: "700", align: "left" },
    }),
  );
  layers.push(
    mkText({
      id: "txt_companyDate",
      top: cTop + 3.9,
      left: cLeft,
      width: cW,
      height: 3.2,
      zIndex: 29,
      text: `Ngày thành lập: ${TEMPLATE_VARS.establishedDate.token}`,
      style: { fontSize: cFs, fontWeight: "300", align: "left" },
    }),
  );
  layers.push(
    mkText({
      id: "txt_companyAddr",
      top: cTop + 6.8,
      left: cLeft,
      width: cW,
      height: 3.2,
      zIndex: 29,
      text: `Địa chỉ: ${TEMPLATE_VARS.businessAddress.token}`,
      style: { fontSize: cFs, fontWeight: "300", align: "left" },
    }),
  );
  layers.push(
    mkText({
      id: "txt_companyPhone",
      top: cTop + 9.7,
      left: cLeft,
      width: cW,
      height: 3.2,
      zIndex: 29,
      text: `Điện thoại: ${TEMPLATE_VARS.companyPhone.token}`,
      style: { fontSize: cFs, fontWeight: "300", align: "left" },
    }),
  );
  layers.push(
    mkText({
      id: "txt_companyEmail",
      top: cTop + 12.6,
      left: cLeft,
      width: cW,
      height: 3.2,
      zIndex: 29,
      text: `Email: ${TEMPLATE_VARS.companyEmail.token}`,
      style: { fontSize: cFs, fontWeight: "300", align: "left" },
    }),
  );
  layers.push(
    mkText({
      id: "txt_companyWeb",
      top: cTop + 15.5,
      left: cLeft,
      width: cW,
      height: 3.2,
      zIndex: 29,
      text: `Website: ${TEMPLATE_VARS.websiteOrHotline.token}`,
      style: { fontSize: cFs, fontWeight: "300", align: "left" },
    }),
  );

  // ===== FOOTER (bottom %) =====
  const fBottom = el.footer?.bottom ?? 4;
  const fW = el.footer?.width ?? 100;
  const fLeft = el.footer?.left ?? 0;
  const fFs = el.footer?.fontSize ?? 18;

  layers.push(
    normalizeLayer({
      id: "txt_footerTitle",
      type: "text",
      bottom: fBottom,
      left: fLeft,
      width: fW,
      height: 4.0,
      zIndex: 30,
      text: t.footerTitle,
      style: {
        fontFamily: FONT_TNR,
        fontSize: fFs,
        fontWeight: "700",
        color: "#fff",
        align: "center",
        uppercase: false,
        lineHeight: 1.25,
      },
    }),
  );

  layers.push(
    normalizeLayer({
      id: "txt_footerValue",
      type: "text",
      bottom: fBottom - 3,
      left: fLeft,
      width: fW,
      height: 4.0,
      zIndex: 30,
      text: TEMPLATE_VARS.businessField.token,
      style: {
        fontFamily: FONT_TNR,
        fontSize: fFs,
        fontWeight: "300",
        color: "#fff",
        align: "center",
        uppercase: false,
        lineHeight: 1.25,
      },
    }),
  );

  s.layers = layers
    .map((l) => normalizeLayer(l))
    .sort((a, b) => Number(a.zIndex) - Number(b.zIndex));
  return s;
}

/* ===== UI primitives ===== */
const Input = (props) => (
  <input
    {...props}
    className={`input input-bordered w-full ${props.className || ""}`}
  />
);
const Select = (props) => (
  <select
    {...props}
    className={`select select-bordered w-full ${props.className || ""}`}
  />
);

const Modal = ({ isOpen, title, onClose, children, footer }) => {
  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="relative bg-white w-full max-w-7xl mx-2 rounded-2xl p-6 shadow-xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            title="Đóng"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">{children}</div>
        {footer && <div className="mt-6">{footer}</div>}
      </div>
    </div>
  );
};

/* ===== ScaledStage ===== */
function useScaledStage(baseWidth = BASE_MD_WIDTH) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [frameSize, setFrameSize] = useState({ w: baseWidth, h: 0 });

  const update = () => {
    const availW = outerRef.current?.clientWidth || baseWidth;
    const s = Math.min(1, availW / baseWidth);
    setScale(s);

    if (innerRef.current) {
      const h = innerRef.current.offsetHeight || 0;
      setFrameSize({ w: baseWidth * s, h: h * s });
    }
  };

  useLayoutEffect(() => {
    update();

    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (outerRef.current && ro) ro.observe(outerRef.current);
    if (innerRef.current && ro) ro.observe(innerRef.current);

    window.addEventListener("resize", update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseWidth]);

  return { outerRef, innerRef, scale, frameSize };
}

function ScaledStage({ children }) {
  const { outerRef, innerRef, scale, frameSize } = useScaledStage();

  return (
    <div ref={outerRef} style={{ width: "100%" }}>
      <div
        style={{
          width: frameSize.w,
          height: frameSize.h,
          margin: "0 auto",
          overflow: "hidden",
        }}
      >
        <div
          ref={innerRef}
          style={{
            width: BASE_MD_WIDTH,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/* ===== Render layers ===== */
function LayerText({ layer }) {
  const st = layer.style || {};
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        fontFamily: st.fontFamily ?? FONT_TNR,
        fontSize: st.fontSize ?? 18,
        fontWeight: st.fontWeight ?? "400",
        color: st.color ?? "#fff",
        textAlign: st.align ?? "left",
        lineHeight: st.lineHeight ?? 1.25,
        textTransform: st.uppercase ? "uppercase" : "none",
        whiteSpace: "pre-wrap",
        overflow: "hidden",
      }}
    >
      {renderTemplatePreviewText(layer.text || "")}
    </div>
  );
}

function LayerImage({ layer, resolvedSrc }) {
  const src = resolvedSrc || layer.src || "";
  const circle = !!layer.circle;

  return (
    <div
      data-bg-src={src}
      style={{
        width: "100%",
        height: "100%",
        borderRadius: circle ? "9999px" : "12px",
        overflow: "hidden",
        backgroundColor: "transparent",
        backgroundImage: src ? `url("${src}")` : "none",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: layer.fit === "contain" ? "contain" : "cover",
      }}
    />
  );
}

/**
 * Render V2 đúng tỷ lệ (ĐỒNG BỘ VỚI UserPDFModal/PageSingleV2):
 * - left/width: % theo WIDTH -> px theo BASE_MD_WIDTH
 * - top/bottom: % theo HEIGHT -> px theo cardHeight (đo từ ảnh nền)
 * - height: ưu tiên % theo HEIGHT (cardHeight) nếu có, fallback theo WIDTH nếu chưa đo được
 */
function PageSingleEditable({
  schema,
  company,
  repUser,
  editorMode = false,
  onCompanyNameEl,
  cardHeight,
  onCardHeightChange,
}) {
  const s = ensureSchemaV2(schema);
  const layers = s.layers || [];
  const bgImgRef = useRef(null);

  const handleBgLoad = () => {
    const h = bgImgRef.current?.offsetHeight || 0;
    onCardHeightChange?.(h);
  };

  useLayoutEffect(() => {
    if (!bgImgRef.current) return;
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            const h = bgImgRef.current?.offsetHeight || 0;
            onCardHeightChange?.(h);
          })
        : null;
    if (ro) ro.observe(bgImgRef.current);
    return () => ro?.disconnect?.();
  }, [onCardHeightChange]);

  const cardH = Number(cardHeight || 0);

  return (
    <div className="relative" style={{ width: BASE_MD_WIDTH }}>
      <img
        ref={bgImgRef}
        src={s.backgroundSrc || DEFAULT_SCHEMA.backgroundSrc}
        className="block w-full h-auto"
        alt=""
        draggable={false}
        onLoad={handleBgLoad}
      />

      {layers
        .slice()
        .sort((a, b) => Number(a.zIndex ?? 1) - Number(b.zIndex ?? 1))
        .map((layer) => {
          const hasBottom = layer.bottom !== undefined && layer.bottom !== null;

          const topPct = Number(layer.top ?? 0);

          const leftPx = (BASE_MD_WIDTH * Number(layer.left ?? 0)) / 100;
          const widthPx = (BASE_MD_WIDTH * Number(layer.width ?? 10)) / 100;

          // IMPORTANT: height đồng bộ với modal/export
          const heightPx =
            layer.type === "image" && layer.circle
              ? widthPx
              : cardH
                ? (cardH * Number(layer.height ?? 6)) / 100
                : (BASE_MD_WIDTH * Number(layer.height ?? 6)) / 100;

          const topPx = !hasBottom && cardH ? (cardH * topPct) / 100 : 0;
          const bottomPx =
            hasBottom && cardH ? (cardH * Number(layer.bottom ?? 0)) / 100 : 0;

          const wrapperStyle = {
            position: "absolute",
            left: `${leftPx}px`,
            width: `${widthPx}px`,
            height: `${heightPx}px`,
            zIndex: layer.zIndex ?? 1,
            pointerEvents: editorMode ? "none" : "auto",
            ...(hasBottom
              ? { bottom: `${bottomPx}px` }
              : { top: `${topPx}px` }),
          };

          if (layer.type === "image") {
            const resolvedSrc = resolveImageSource(
              layer.sourceKey,
              s,
              company,
              repUser,
            );
            return (
              <div key={layer.id} style={wrapperStyle}>
                <LayerImage layer={layer} resolvedSrc={resolvedSrc} />
              </div>
            );
          }

          const isCompanyNameLayer = layer.id === "txt_companyName";
          return (
            <div
              key={layer.id}
              style={wrapperStyle}
              ref={
                isCompanyNameLayer ? (el) => onCompanyNameEl?.(el) : undefined
              }
            >
              <LayerText layer={layer} />
            </div>
          );
        })}
    </div>
  );
}

/* ===== Drag overlay ===== */
function useDrag({ onStart, onMove, onEnd }) {
  const draggingRef = useRef(false);
  const startRef = useRef({ x: 0, y: 0 });

  const onPointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    draggingRef.current = true;
    startRef.current = { x: e.clientX, y: e.clientY };

    onStart?.(e);

    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    e.preventDefault();

    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;
    startRef.current = { x: e.clientX, y: e.clientY };

    onMove?.({ dx, dy });
  };

  const onPointerUp = (e) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    onEnd?.(e);
  };

  return { onPointerDown, onPointerMove, onPointerUp };
}

const LayerBox = ({ layer, selected, onSelect, onDrag, cardHeight }) => {
  const hasBottom = layer.bottom !== undefined && layer.bottom !== null;
  const isCircleImg = layer.type === "image" && !!layer.circle;

  const topPct = Number(layer.top ?? 0);

  const leftPx = (BASE_MD_WIDTH * Number(layer.left ?? 0)) / 100;
  const widthPx = (BASE_MD_WIDTH * Number(layer.width ?? 10)) / 100;

  // IMPORTANT: height đồng bộ với modal/export
  const heightPx = isCircleImg
    ? widthPx
    : cardHeight
      ? (cardHeight * Number(layer.height ?? 6)) / 100
      : (BASE_MD_WIDTH * Number(layer.height ?? 6)) / 100;

  const topPx = !hasBottom && cardHeight ? (cardHeight * topPct) / 100 : 0;
  const bottomPx =
    hasBottom && cardHeight
      ? (cardHeight * Number(layer.bottom ?? 0)) / 100
      : 0;

  const drag = useDrag({
    onStart: () => onSelect?.(layer.id),
    onMove: onDrag,
  });

  return (
    <div
      className="absolute"
      style={{
        left: `${leftPx}px`,
        width: `${widthPx}px`,
        height: `${heightPx}px`,
        top: !hasBottom ? `${topPx}px` : undefined,
        bottom: hasBottom ? `${bottomPx}px` : undefined,
        zIndex: 9999,
      }}
      {...drag}
    >
      <div
        className="absolute inset-0"
        style={{
          border: selected
            ? "2px solid #3b82f6"
            : "1px dashed rgba(255,255,255,0.55)",
          borderRadius: 10,
          background: selected ? "rgba(59,130,246,0.10)" : "rgba(0,0,0,0.02)",
          cursor: "move",
          touchAction: "none",
          userSelect: "none",
        }}
      />
      {selected && (
        <div
          className="absolute -top-7 left-0 px-2 py-1 text-xs rounded"
          style={{ background: "#3b82f6", color: "white" }}
        >
          {layer.type === "image" ? "Ảnh" : "Text"} · {layer.id}
        </div>
      )}
    </div>
  );
};

function TemplateEditorStage({
  schema,
  setSchema,
  selected,
  setSelected,
  company,
  repUser,
}) {
  const cardRef = useRef(null);

  const s = ensureSchemaV2(schema);
  const layers = s.layers || [];

  const [cardHeight, setCardHeight] = useState(0);

  const updateLayer = (id, patch) => {
    setSchema((prev) => {
      const next = ensureSchemaV2(prev);
      next.layers = (next.layers || []).map((l) => {
        if (l.id !== id) return l;
        return normalizeLayer({ ...l, ...patch });
      });
      return next;
    });
  };

  const updateByDrag = (id, { dx, dy }) => {
    const base = cardRef.current;
    if (!base) return;

    const rect = base.getBoundingClientRect();
    const baseW = rect.width; // scaled width
    const baseH = rect.height; // scaled height

    const cur = layers.find((x) => x.id === id);
    if (!cur) return;

    const dLeftPct = toPct(dx, baseW); // left % theo width
    const dTopPct = toPct(dy, baseH); // top/bottom % theo height

    const nextLeft = clamp((cur.left ?? 0) + dLeftPct, 0, 100);

    if (cur.bottom !== undefined && cur.bottom !== null) {
      const nextBottom = clamp((cur.bottom ?? 0) - dTopPct, 0, 100);
      updateLayer(id, { left: nextLeft, bottom: nextBottom });
    } else {
      const nextTop = clamp((cur.top ?? 0) + dTopPct, 0, 100);
      updateLayer(id, { left: nextLeft, top: nextTop });
    }
  };

  return (
    <div className="w-full" onClick={() => setSelected(null)}>
      <ScaledStage>
        <div
          ref={cardRef}
          className="relative"
          style={{ width: BASE_MD_WIDTH }}
          onClick={(e) => e.stopPropagation()}
        >
          <PageSingleEditable
            schema={s}
            company={company}
            repUser={repUser}
            editorMode
            cardHeight={cardHeight}
            onCardHeightChange={(h) => setCardHeight(h)}
          />

          {layers.map((layer) => (
            <LayerBox
              key={layer.id}
              layer={layer}
              selected={selected === layer.id}
              onSelect={setSelected}
              onDrag={(d) => updateByDrag(layer.id, d)}
              cardHeight={cardHeight}
            />
          ))}
        </div>
      </ScaledStage>
    </div>
  );
}

/* ===== Right panel controls ===== */
function ElementControls({
  schema,
  setSchema,
  selected,
  setSelected,
  company,
  repUser,
}) {
  const s = ensureSchemaV2(schema);
  const layers = s.layers || [];
  const layer = layers.find((l) => l.id === selected);

  const setLayer = (patch) => {
    if (!selected) return;
    setSchema((prev) => {
      const next = ensureSchemaV2(prev);
      next.layers = (next.layers || []).map((l) => {
        if (l.id !== selected) return l;
        return normalizeLayer({ ...l, ...patch });
      });
      return next;
    });
  };

  const deleteLayer = () => {
    if (!selected) return;
    setSchema((prev) => {
      const next = ensureSchemaV2(prev);
      next.layers = (next.layers || []).filter((l) => l.id !== selected);
      return next;
    });
    setSelected?.(null);
  };

  const [quickFieldKey, setQuickFieldKey] = useState(
    FIELD_DEFS[0]?.key || "rep.userFullName",
  );

  const insertFieldAsText = () => {
    const def =
      FIELD_DEFS.find((x) => x.key === quickFieldKey) || FIELD_DEFS[0];

    // IMPORTANT: lưu token, không lấy data
    const token = resolveFieldToken(def.key);

    const newLayer = normalizeLayer({
      id: genId("txt"),
      type: "text",
      top: 60,
      left: 10,
      width: 80,
      height: 4,
      zIndex: (layers?.length || 0) + 50,
      text: `${def.prefix || ""}${token}`,
      style: {
        fontFamily: FONT_TNR,
        fontSize: 18,
        fontWeight: "400",
        color: "#ffffff",
        align: "left",
        uppercase: false,
        lineHeight: 1.25,
      },
    });

    setSchema((prev) => {
      const next = ensureSchemaV2(prev);
      next.layers = [...(next.layers || []), newLayer];
      return next;
    });

    setSelected?.(newLayer.id);
    toast.info(
      "Đã chèn thông tin vào thiệp (lưu token, preview hiển thị dạng [..]).",
    );
  };

  const applySelectedFromField = (fieldKey) => {
    if (!layer || layer.type !== "text") return;
    const def = FIELD_DEFS.find((x) => x.key === fieldKey);
    if (!def) return;

    // IMPORTANT: lưu token, không lấy data
    const token = resolveFieldToken(def.key);
    setLayer({ text: `${def.prefix || ""}${token}` });
    toast.info("Đã áp dụng nội dung dạng token (preview hiển thị dạng [..]).");
  };

  const [selectedFieldKey, setSelectedFieldKey] = useState(
    FIELD_DEFS[0]?.key || "rep.userFullName",
  );
  useEffect(() => {
    setSelectedFieldKey(FIELD_DEFS[0]?.key || "rep.userFullName");
  }, [selected]);

  if (!selected || !layer) {
    return (
      <div className="space-y-4">
        <div className="text-sm text-gray-500">
          Chọn một khối trên thiệp để chỉnh (click vào khung nét đứt).
        </div>

        <div className="border-t pt-4 space-y-2">
          <div className="font-semibold">Chèn thông tin vào thiệp</div>
          <Select
            value={quickFieldKey}
            onChange={(e) => setQuickFieldKey(e.target.value)}
          >
            {FIELD_DEFS.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
          </Select>
          <button
            className="btn btn-outline btn-primary w-full"
            onClick={insertFieldAsText}
          >
            <PlusIcon className="w-5 h-5 mr-1" />
            Chèn vào thiệp
          </button>

          <div className="text-xs text-gray-500">
            Lưu ý: Editor hiển thị dạng thông tin (trong ngoặc []) nhưng khi
            hiển thị sẽ hiển thị thông tin của hội viên.
          </div>
        </div>
      </div>
    );
  }

  const hasBottom = layer.bottom !== undefined && layer.bottom !== null;
  const st = layer.type === "text" ? layer.style || {} : null;
  const isCircleImg = layer.type === "image" && !!layer.circle;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="font-semibold">
          Đang chỉnh:{" "}
          <span className="text-blue-600">{layer.type.toUpperCase()}</span>
        </div>

        <button
          className="btn btn-outline btn-error btn-sm"
          onClick={deleteLayer}
          title="Xoá khối này"
        >
          <TrashIcon className="w-4 h-4 mr-1" />
          Xoá
        </button>
      </div>

      {/* Position / size */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="label">
            <span className="label-text">Left (%)</span>
          </div>
          <Input
            type="number"
            value={layer.left ?? 0}
            onChange={(e) => setLayer({ left: Number(e.target.value) })}
          />
        </div>

        {!hasBottom ? (
          <div>
            <div className="label">
              <span className="label-text">Top (%)</span>
            </div>
            <Input
              type="number"
              value={layer.top ?? 0}
              onChange={(e) => setLayer({ top: Number(e.target.value) })}
            />
          </div>
        ) : (
          <div>
            <div className="label">
              <span className="label-text">Bottom (%)</span>
            </div>
            <Input
              type="number"
              value={layer.bottom ?? 0}
              onChange={(e) => setLayer({ bottom: Number(e.target.value) })}
            />
          </div>
        )}

        <div className="col-span-2">
          <div className="label">
            <span className="label-text">Width (% theo chiều rộng)</span>
          </div>
          <Input
            type="number"
            value={layer.width ?? 20}
            onChange={(e) => {
              const w = Number(e.target.value);
              if (isCircleImg) setLayer({ width: w, height: w });
              else setLayer({ width: w });
            }}
          />
        </div>

        <div className="col-span-2">
          <div className="label">
            <span className="label-text">Height (% theo chiều cao)</span>
          </div>
          <Input
            type="number"
            value={isCircleImg ? (layer.width ?? 20) : (layer.height ?? 6)}
            disabled={isCircleImg}
            onChange={(e) => setLayer({ height: Number(e.target.value) })}
          />
          {isCircleImg && (
            <div className="text-xs text-gray-500 mt-1">
              Ảnh bo tròn luôn 1:1 theo Width.
            </div>
          )}
        </div>

        <div className="col-span-2">
          <div className="label">
            <span className="label-text">Z-index</span>
          </div>
          <Input
            type="number"
            value={layer.zIndex ?? 10}
            onChange={(e) => setLayer({ zIndex: Number(e.target.value) })}
          />
        </div>
      </div>

      {/* Type-specific */}
      {layer.type === "image" ? (
        <div className="space-y-2 pt-2 border-t">
          <div className="font-semibold">Ảnh</div>

          <div>
            <div className="label">
              <span className="label-text">Nguồn ảnh nhanh</span>
            </div>
            <Select
              value={layer.sourceKey ?? ""}
              onChange={(e) => setLayer({ sourceKey: e.target.value })}
            >
              {IMAGE_SOURCES.map((x) => (
                <option key={x.key} value={x.key}>
                  {x.label}
                </option>
              ))}
            </Select>
          </div>

          {!(layer.sourceKey || "").trim() && (
            <div>
              <div className="label">
                <span className="label-text">URL ảnh</span>
              </div>
              <Input
                value={layer.src ?? ""}
                onChange={(e) => setLayer({ src: e.target.value })}
                placeholder="https://..."
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="label">
                <span className="label-text">Fit</span>
              </div>
              <Select
                value={layer.fit === "contain" ? "contain" : "cover"}
                onChange={(e) => setLayer({ fit: e.target.value })}
              >
                <option value="cover">Cover</option>
                <option value="contain">Contain</option>
              </Select>
            </div>

            <div className="flex items-end">
              <label className="label cursor-pointer gap-2">
                <span className="label-text">Bo tròn</span>
                <input
                  type="checkbox"
                  className="toggle toggle-primary"
                  checked={!!layer.circle}
                  onChange={(e) => {
                    const c = e.target.checked;
                    if (c)
                      setLayer({ circle: true, height: layer.width ?? 20 });
                    else setLayer({ circle: false });
                  }}
                />
              </label>
            </div>
          </div>

          <div className="text-xs text-gray-500 break-words">
            Preview src:{" "}
            <span className="text-gray-700">
              {resolveImageSource(layer.sourceKey, s, company, repUser) ||
                layer.src ||
                "—"}
            </span>
          </div>
        </div>
      ) : (
        <div className="space-y-2 pt-2 border-t">
          <div className="font-semibold">Text</div>

          <div className="p-3 rounded-lg border bg-base-100 space-y-2">
            <div className="font-semibold text-sm">
              Chèn biến (token) vào khối đang chọn
            </div>
            <Select
              value={selectedFieldKey}
              onChange={(e) => setSelectedFieldKey(e.target.value)}
            >
              {FIELD_DEFS.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </Select>
            <button
              className="btn btn-outline btn-primary w-full"
              onClick={() => applySelectedFromField(selectedFieldKey)}
            >
              Áp dụng vào khối đang chọn
            </button>
          </div>

          <div>
            <div className="label">
              <span className="label-text">Nội dung (lưu token)</span>
            </div>
            <Input
              value={layer.text ?? ""}
              onChange={(e) => setLayer({ text: e.target.value })}
            />
            <div className="text-xs text-gray-500 mt-1">
              Preview hiển thị:{" "}
              <span className="text-gray-700">
                {renderTemplatePreviewText(layer.text ?? "")}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="label">
                <span className="label-text">Font size</span>
              </div>
              <Input
                type="number"
                value={Number(st.fontSize ?? 18)}
                onChange={(e) =>
                  setLayer({
                    style: { ...st, fontSize: Number(e.target.value) },
                  })
                }
              />
            </div>

            <div>
              <div className="label">
                <span className="label-text">Font weight</span>
              </div>
              <Select
                value={String(st.fontWeight ?? "400")}
                onChange={(e) =>
                  setLayer({ style: { ...st, fontWeight: e.target.value } })
                }
              >
                {[
                  "100",
                  "200",
                  "300",
                  "400",
                  "500",
                  "600",
                  "700",
                  "800",
                  "900",
                ].map((w) => (
                  <option key={w} value={w}>
                    {w}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <div className="label">
                <span className="label-text">Align</span>
              </div>
              <Select
                value={st.align ?? "left"}
                onChange={(e) =>
                  setLayer({ style: { ...st, align: e.target.value } })
                }
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </Select>
            </div>

            <div>
              <div className="label">
                <span className="label-text">Màu chữ</span>
              </div>
              <Input
                value={st.color ?? "#ffffff"}
                onChange={(e) =>
                  setLayer({ style: { ...st, color: e.target.value } })
                }
                placeholder="#ffffff"
              />
            </div>

            <div className="col-span-2">
              <div className="label">
                <span className="label-text">Font family</span>
              </div>
              <Input
                value={st.fontFamily ?? FONT_TNR}
                onChange={(e) =>
                  setLayer({ style: { ...st, fontFamily: e.target.value } })
                }
              />
            </div>

            <div className="col-span-2">
              <div className="label">
                <span className="label-text">Line height</span>
              </div>
              <Input
                type="number"
                step="0.05"
                value={Number(st.lineHeight ?? 1.25)}
                onChange={(e) =>
                  setLayer({
                    style: { ...st, lineHeight: Number(e.target.value) },
                  })
                }
              />
            </div>

            <div className="col-span-2">
              <label className="label cursor-pointer gap-2">
                <span className="label-text">Uppercase</span>
                <input
                  type="checkbox"
                  className="toggle toggle-primary"
                  checked={!!st.uppercase}
                  onChange={(e) =>
                    setLayer({ style: { ...st, uppercase: e.target.checked } })
                  }
                />
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ========================= Main modal ========================= */
const WelcomeCardMemberEditorModal = ({
  isOpen,
  onClose,
  company,
  repUser,
}) => {
  const canUpdate = hasPermission("WELCOME_CARD_UPDATE");
  const companyId = company?.id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [selected, setSelected] = useState(null);
  const [schema, setSchema] = useState(() => ensureSchemaV2(null));

  const baseActiveRef = useRef(ensureSchemaV2(null)); // reset về active

  const loadData = async () => {
    if (!isOpen) return;
    if (!canUpdate) return;

    if (!companyId) {
      toast.error("Thiếu companyId để chỉnh thiệp riêng");
      return;
    }

    setLoading(true);
    setSelected(null);

    try {
      // 1) active template
      const res = await ApiCustom.get(API.listTemplates);
      const list = extractList(res);
      const activeTpl = pickActiveTemplate(list);
      const activeSchema = ensureSchemaV2(
        mergeSchema(safeParseSchema(safeParseSchema(activeTpl?.schema))),
      );

      baseActiveRef.current = activeSchema;

      // 2) member override
      let override = null;
      try {
        const resOv = await ApiCustom.get(API.getMemberOverride(companyId));
        const d = resOv?.data?.data ?? resOv?.data ?? null;
        const raw = d?.schema ?? d;
        override = safeParseSchema(safeParseSchema(raw));
      } catch {
        override = null;
      }

      const ovSchema = override ? ensureSchemaV2(mergeSchema(override)) : null;

      // 3) merge: ưu tiên override cho layers nếu có
      const merged = ensureSchemaV2({
        ...activeSchema,
        ...(ovSchema || {}),
        elements: {
          ...(activeSchema.elements || {}),
          ...(ovSchema?.elements || {}),
        },
        assets: { ...(activeSchema.assets || {}), ...(ovSchema?.assets || {}) },
        memberBlock: {
          ...(activeSchema.memberBlock || {}),
          ...(ovSchema?.memberBlock || {}),
        },
        texts: { ...(activeSchema.texts || {}), ...(ovSchema?.texts || {}) },
        layers:
          Array.isArray(ovSchema?.layers) && ovSchema.layers.length
            ? ovSchema.layers
            : activeSchema.layers,
      });

      setSchema(merged);
    } catch (e) {
      toast.error(
        e?.response?.data?.message || "Không tải được thiệp theo hội viên",
      );
      setSchema(ensureSchemaV2(null));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, companyId]);

  const resetToActive = () => {
    setSchema(JSON.parse(JSON.stringify(baseActiveRef.current)));
    setSelected(null);
    toast.info("Đã reset về mẫu đang dùng");
  };

  const addText = () => {
    setSchema((prev) => {
      const next = ensureSchemaV2(prev);
      const newLayer = normalizeLayer({
        id: genId("txt"),
        type: "text",
        top: 60,
        left: 10,
        width: 80,
        height: 4,
        zIndex: (next.layers?.length || 0) + 50,
        text: "Text mới",
        style: {
          fontFamily: FONT_TNR,
          fontSize: 18,
          fontWeight: "600",
          color: "#ffffff",
          align: "left",
          uppercase: false,
          lineHeight: 1.25,
        },
      });
      next.layers = [...(next.layers || []), newLayer];
      return next;
    });
    toast.info("Đã thêm Text.");
  };

  const addImage = () => {
    setSchema((prev) => {
      const next = ensureSchemaV2(prev);
      const newLayer = normalizeLayer({
        id: genId("img"),
        type: "image",
        top: 60,
        left: 10,
        width: 20,
        height: 12,
        zIndex: (next.layers?.length || 0) + 50,
        sourceKey: "",
        src: "",
        fit: "cover",
        circle: false,
      });
      next.layers = [...(next.layers || []), newLayer];
      return next;
    });
    toast.info("Đã thêm Ảnh.");
  };

  const save = async () => {
    if (!companyId) return toast.error("Thiếu companyId");
    try {
      setSaving(true);
      const payload = ensureSchemaV2(schema);
      await ApiCustom.put(
        API.saveMemberOverride(companyId),
        { schema: JSON.stringify(payload) },
        JSON_HEADERS,
      );
      toast.success("Đã lưu thiệp riêng cho hội viên");
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Lưu thất bại");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  if (!canUpdate) {
    return (
      <Modal
        isOpen={isOpen}
        title="Chỉnh sửa thiệp theo hội viên"
        onClose={onClose}
      >
        <div className="alert alert-warning">Bạn không có quyền chỉnh sửa.</div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      title={`Chỉnh sửa thiệp · ${company?.companyName || "Hội viên"}`}
      onClose={onClose}
      footer={
        <div className="flex justify-between items-center gap-2 flex-wrap">
          <div className="flex gap-2 flex-wrap">
            <button
              className="btn btn-outline"
              onClick={resetToActive}
              disabled={loading || saving}
            >
              <ArrowPathIcon className="w-5 h-5 mr-1" />
              Reset về mẫu đang dùng
            </button>

            <button
              className="btn btn-outline btn-primary"
              onClick={addText}
              disabled={loading || saving}
            >
              <PlusIcon className="w-5 h-5 mr-1" />
              Thêm Text
            </button>

            <button
              className="btn btn-outline btn-info"
              onClick={addImage}
              disabled={loading || saving}
            >
              <PlusIcon className="w-5 h-5 mr-1" />
              Thêm Ảnh
            </button>
          </div>

          <div className="flex gap-2">
            <button
              className="btn btn-ghost"
              onClick={onClose}
              disabled={saving}
            >
              Huỷ
            </button>
            <button
              className="btn btn-primary"
              onClick={save}
              disabled={loading || saving}
            >
              {saving ? "Đang lưu..." : "Lưu"}
            </button>
          </div>
        </div>
      }
    >
      {loading ? (
        <div className="text-gray-500">Đang tải…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Left */}
          <div className="lg:col-span-8">
            <div className="p-3 rounded-xl border bg-base-100">
              <TemplateEditorStage
                schema={schema}
                setSchema={setSchema}
                selected={selected}
                setSelected={setSelected}
                company={company}
                repUser={repUser}
              />
              <div className="mt-2 text-xs text-gray-500">
                Mẹo: Bấm vào khung nét đứt để chọn. Giữ/kéo để di chuyển. Bên
                phải chỉnh chi tiết (URL, text, font…).
                <br />
                Lưu ý: Nội dung dạng biến sẽ hiển thị preview trong ngoặc []
                nhưng khi lưu sẽ là thông tin của hội viên.
              </div>
            </div>
          </div>

          {/* Right */}
          <div className="lg:col-span-4">
            <div className="p-4 rounded-xl border bg-base-100 space-y-4">
              <div className="font-semibold">Tuỳ chỉnh</div>
              <div className="border-t pt-4">
                <ElementControls
                  schema={schema}
                  setSchema={setSchema}
                  selected={selected}
                  setSelected={setSelected}
                  company={company}
                  repUser={repUser}
                />
              </div>

              <div className="text-xs text-gray-500">
                Ghi chú: Width là % theo chiều rộng; Height là % theo chiều cao.
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default WelcomeCardMemberEditorModal;
